import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { BILLING_DISPLAY_TIMEZONE, type BillingStatusValue } from '@/pages/Billing/utils'

dayjs.extend(utc)
dayjs.extend(timezone)

// Copied presentational values (pages/Billing pill palette) — decision: no
// legacy↔v2 component imports across page modules.
export type StatusPillStyle = { backgroundColor: string; borderColor: string; color: string }

export const PILL_STYLE_GREEN: StatusPillStyle = {
  backgroundColor: '#e8fff1',
  borderColor: '#c9f2d8',
  color: '#31a24c',
}

export const PILL_STYLE_AMBER: StatusPillStyle = {
  backgroundColor: '#fff4e8',
  borderColor: '#f6d8b8',
  color: '#d97706',
}

export const PILL_STYLE_RED: StatusPillStyle = {
  backgroundColor: '#fff1f2',
  borderColor: '#fecdd3',
  color: '#e11d48',
}

export const PILL_STYLE_GRAY: StatusPillStyle = {
  backgroundColor: '#f8fafc',
  borderColor: '#e2e8f0',
  color: '#64748b',
}

export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

// Routing column labels: known routes get a short label, 'none' reads "Not set" on
// the route chip (never blank); any unknown/future value passes through raw.
export const ROUTING_LABELS: Record<string, string> = {
  bridge: 'Bridge',
  stedi: 'Stedi',
  manual: 'Manual',
  none: 'Not set',
}

export function formatRoutingLabel(route: string): string {
  return ROUTING_LABELS[route] ?? route
}

// Calendar dates (YYYY-MM-DD, pg `date` columns) cross the wire as-is — format
// without any timezone conversion.
export function formatCalendarDate(date: string): string {
  return dayjs(date).format('MM/DD/YYYY')
}

/**
 * Which claim statuses the user may set from the current one — the UI mirror of the
 * backend gate (middleware billing-v2/claim-status-transitions.ts, which stays the
 * enforcer): only Submitted/Denied/Voided are manual, Submitted/Denied never on a paid
 * case, Voided only when a payment counted, and a voided case is final (empty list →
 * render a static pill).
 */
export function manualStatusOptions(
  current: BillingStatusValue,
  hasPayments: boolean,
): BillingStatusValue[] {
  if (current === 'voided') return []
  if (current === 'paid') return hasPayments ? ['voided'] : []
  const options: BillingStatusValue[] = ['billed', 'denied']
  if (hasPayments) options.push('voided')
  return options
}

/**
 * Picker date (YYYY-MM-DD) → ISO instant pinned to midnight in the billing display
 * timezone, the value the legacy updated-at extension expects (slim port of the legacy
 * pages/Billing/service.ts normalizer, which is unexported and covers input shapes the
 * Mantine 8 picker no longer produces).
 */
export function normalizeBillingUpdateDate(date: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error('billingUpdateDate must be a valid date')
  }
  const pinned = dayjs.tz(`${date} 00:00:00`, BILLING_DISPLAY_TIMEZONE)
  if (!pinned.isValid()) {
    throw new Error('billingUpdateDate must be a valid date')
  }
  return pinned.toISOString()
}

/** Today as a picker value (YYYY-MM-DD) in the billing display timezone. */
export function todayBillingDate(): string {
  return dayjs().tz(BILLING_DISPLAY_TIMEZONE).format('YYYY-MM-DD')
}
